/**
 * Раздел «Земельный фонд» страницы «Аналитика» (US-04.6): верхняя плашка
 * из 5 показателей (всего земель, пашни, пастбища, земли запаса, сельхоз.
 * предприятия) + два виджета-разреза в ряд («Земли населённых пунктов»,
 * «Земли запаса») + широкая таблица «Крупные землевладельцы»
 * с двухуровневой шапкой (закреплено vs фактическое использование,
 * по каждому — пашня / пастбище).
 *
 * Показатели плашки видны всегда (без данных — «—», замечание ревью);
 * карточки равномерно
 * растягиваются по ширине (FR-2.4); виджет без данных не рендерится
 * (Сценарий 8). Drill-through (FR-4) — вне текущей вёрстки: детализация
 * первичных данных появится вместе с бэком.
 *
 * Источник данных — гибрид: демо-КАТО (HARD_DATA_KATOS)
 * → мок hardData/analyticsLandFund.json (тыс. га; «итого» вычисляются
 * на фронте, FR-2.3/FR-3.5); остальные КАТО (по выбранному в сайдбаре) —
 * один запрос /api/public/analytics/land-fund (DP-136, US-04.6):
 * plashka → плашка, settlement → «Земли населённых пунктов», reserve →
 * «Земли запаса», landowners → «Крупные землевладельцы». Площади API —
 * в га, лейбл на фронте — «тыс. га» (÷1000); «итого» приходят готовыми.
 */
import { LandPlot } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { StatCard } from '../../../components/widgets/StatCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import analyticsLandFund from '../../../hardData/analyticsLandFund.json';
import { getAnalyticsLandFund } from '../../../requests/analytics.request';
import { useAnalyticsSection } from '../useAnalyticsSection';
import type { LandFundAnalyticsResponse } from '../../../types/analytics';

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

/** Сумма компонент; все null → null (в плашке показывается «—»). */
const sum = (...parts: Array<number | null>): number | null => {
  const known = parts.filter((v): v is number => v !== null);
  return known.length === 0
    ? null
    : known.reduce((acc, v) => acc + v, 0);
};

/** Га из API → тыс. га для отображения (лейбл «тыс. га» на фронте). */
const toThsHa = (v: number | null): number | null =>
  v !== null ? v / 1000 : null;

interface LandBarRowVM {
  key: string;
  value: string;
  unit: string;
  /** null — строка без бара (другая единица, например «ед.»). */
  barPercent: number | null;
}

interface LandownerVM {
  key: string;
  name: string;
  assignedArable: string;
  assignedPasture: string;
  factArable: string;
  factPasture: string;
}

interface LandFundSectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевой виджет —
   * «Земли населённых пунктов» (FR-2.2). */
  summary?: boolean;
}

export const LandFundSection = ({
  summary = false,
}: LandFundSectionProps = {}) => {
  const { t } = useTranslation();

  const { data, loading, isHardKato } =
    useAnalyticsSection<LandFundAnalyticsResponse>(
      'landFund',
      getAnalyticsLandFund,
    );

  const thousandHa = t('analytics.landFund.unitThousandHa');
  const pieces = t('units.pieces');

  // --- Значения в тыс. га: мок — как есть (агрегаты вычисляются,
  // FR-2.3/FR-3.5), API — га ÷ 1000 («итого» приходят готовыми) ---

  const {
    settlementLand,
    agricultural_arable: mockAgriArable,
    agricultural_pasture: mockAgriPasture,
    agricultural_farmEnterprises: mockFarmEnterprises,
    reserve_arable: mockReserveArable,
    reserve_pasture: mockReservePasture,
    otherLand: mockOtherLand,
  } = analyticsLandFund.landFundItems;

  const settlement = data?.settlement ?? null;
  const reserve = data?.reserve ?? null;
  const plashka = data?.plashka;

  const agriArable = isHardKato
    ? mockAgriArable
    : toThsHa(settlement?.agricultural_arable_ha ?? null);
  const agriPasture = isHardKato
    ? mockAgriPasture
    : toThsHa(settlement?.agricultural_pasture_ha ?? null);
  const agriTotal = isHardKato
    ? sum(agriArable, agriPasture)
    : toThsHa(settlement?.agricultural_total_ha ?? null);
  const farmEnterprises = isHardKato
    ? mockFarmEnterprises
    : (settlement?.farm_enterprises ?? plashka?.farm_enterprises ?? null);

  const reserveArable = isHardKato
    ? mockReserveArable
    : toThsHa(reserve?.reserve_arable_ha ?? null);
  const reservePasture = isHardKato
    ? mockReservePasture
    : toThsHa(reserve?.reserve_pasture_ha ?? null);
  const reserveTotal = isHardKato
    ? sum(reserveArable, reservePasture)
    : toThsHa(reserve?.reserve_total_ha ?? plashka?.reserve_total_ha ?? null);
  const otherLand = isHardKato
    ? mockOtherLand
    : toThsHa(reserve?.other_land_ha ?? null);

  const totalLand = isHardKato
    ? sum(settlementLand, reserveTotal, otherLand)
    : toThsHa(plashka?.total_land_ha ?? null);
  const plashkaArable = isHardKato
    ? agriArable
    : toThsHa(plashka?.arable_ha ?? null);
  const plashkaPasture = isHardKato
    ? agriPasture
    : toThsHa(plashka?.pasture_ha ?? null);

  // Плашка всегда показывает все показатели; нет данных → «—»
  // (замечание дизайн-ревью).
  const landCell = (v: number | null, whole: boolean): string =>
    v === null ? '—' : whole ? fmt(v) : fmt1(v);
  const cards: Array<{ key: string; value: string; unit: string }> = [
    { key: 'totalLand', value: landCell(totalLand, false), unit: thousandHa },
    { key: 'arable', value: landCell(plashkaArable, false), unit: thousandHa },
    {
      key: 'pasture',
      value: landCell(plashkaPasture, false),
      unit: thousandHa,
    },
    {
      key: 'reserveLand',
      value: landCell(reserveTotal, false),
      unit: thousandHa,
    },
    {
      key: 'farmEnterprises',
      value: landCell(farmEnterprises, true),
      unit: pieces,
    },
  ];

  // Виджеты-разрезы: бары — доля от максимума среди га-строк виджета;
  // строка в других единицах («ед.») идёт без бара.
  const toBars = (
    rows: Array<{ key: string; value: number | null; unit?: string }>,
  ): LandBarRowVM[] => {
    const known = rows.filter(
      (row): row is { key: string; value: number; unit?: string } =>
        row.value !== null,
    );
    const max = Math.max(
      1,
      ...known.filter((row) => row.unit === undefined).map((row) => row.value),
    );
    return known.map((row) => ({
      key: row.key,
      value: row.unit === undefined ? fmt1(row.value) : fmt(row.value),
      unit: row.unit ?? thousandHa,
      barPercent:
        row.unit === undefined ? Math.max(2, (row.value / max) * 100) : null,
    }));
  };

  const settlementRows = toBars([
    { key: 'agriTotal', value: agriTotal },
    { key: 'agriArable', value: agriArable },
    { key: 'agriPasture', value: agriPasture },
    { key: 'farmEnterprises', value: farmEnterprises, unit: pieces },
  ]);

  const reserveRows = toBars([
    { key: 'total', value: reserveTotal },
    { key: 'arable', value: reserveArable },
    { key: 'pasture', value: reservePasture },
    { key: 'otherLand', value: otherLand },
  ]);

  const widgetDefs: Array<{
    key: 'settlementLands' | 'reserveLands';
    rows: LandBarRowVM[];
    testId: string;
  }> = [
    {
      key: 'settlementLands',
      rows: settlementRows,
      testId: 'analytics-landfund-settlement',
    },
    {
      key: 'reserveLands',
      rows: reserveRows,
      testId: 'analytics-landfund-reserve',
    },
  ];
  // Виджеты всегда видны (без данных — «Нет данных», замечание ревью);
  // на витрине «Все» остаётся только «Земли населённых пунктов»
  // (US-04.1 FR-2.2).
  const widgets = widgetDefs.filter(
    (widget) => !summary || widget.key === 'settlementLands',
  );

  // «Крупные землевладельцы»: демо-КАТО — мок (тыс. га как есть),
  // остальные — landowners ответа (га ÷ 1000; нет значения → «—»).
  const haCell = (v: number | null): string => (v !== null ? fmt1(v) : '—');
  const landowners: LandownerVM[] = isHardKato
    ? analyticsLandFund.largeLandowners.map((owner) => ({
        key: owner.key,
        name: owner.name,
        assignedArable: fmt1(owner.assignedArable),
        assignedPasture: fmt1(owner.assignedPasture),
        factArable: fmt1(owner.factArable),
        factPasture: fmt1(owner.factPasture),
      }))
    : (data?.landowners ?? []).map((owner, index) => ({
        key: String(index),
        name: owner.name,
        assignedArable: haCell(toThsHa(owner.assigned_arable_ha)),
        assignedPasture: haCell(toThsHa(owner.assigned_pasture_ha)),
        factArable: haCell(toThsHa(owner.fact_arable_ha)),
        factPasture: haCell(toThsHa(owner.fact_pasture_ha)),
      }));

  return (
    <section className="space-y-4" data-testid="analytics-landfund">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.landFund.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <LandPlot
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Пока грузятся данные с бэка — лоадер вместо виджетов. */}
      {loading ? (
        <BlockLoader />
      ) : (
        <>

      {/* Плашка: все показатели всегда видны, без данных — «—». */}
      {!summary && (
        <div className="flex flex-wrap gap-3.5">
          {cards.map((card) => (
            <div key={card.key} className="min-w-52 flex-1">
              <StatCard
                testId={`analytics-card-${card.key}`}
                title={t(`analytics.landFund.cards.${card.key}`)}
                value={card.value}
                unit={card.unit}
              />
            </div>
          ))}
        </div>
      )}

      {widgets.length > 0 && (
        <div className="grid gap-3.5 xl:grid-cols-2">
          {widgets.map((widget) => (
            <Card key={widget.key} className="shadow-card">
              <CardHeader className="flex-row items-baseline justify-between gap-4 space-y-0">
                <CardTitle>
                  {t(`analytics.landFund.${widget.key}.title`)}
                </CardTitle>
                <span className="shrink-0 text-sm text-text-faint">
                  {thousandHa}
                </span>
              </CardHeader>
              <CardContent>
                {widget.rows.length === 0 ? (
                  <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
                ) : (
                <ul data-testid={widget.testId}>
                  {widget.rows.map((row) => (
                    <li
                      key={row.key}
                      data-testid={`${widget.testId}-${row.key}`}
                      className="flex items-center gap-4 border-b border-surface-border py-3.5 text-sm last:border-b-0 last:pb-0"
                    >
                      <span className="min-w-0 flex-1 font-medium leading-snug">
                        {t(`analytics.landFund.${widget.key}.rows.${row.key}`)}
                      </span>
                      {row.barPercent !== null && (
                        <span className="h-4 w-1/3 shrink-0 overflow-hidden rounded-full bg-surface-muted">
                          <span
                            className="block h-full rounded-full bg-chart-blue"
                            style={{ width: `${row.barPercent}%` }}
                          />
                        </span>
                      )}
                      <span className="shrink-0 text-right">
                        <span className="font-extrabold tabular-nums">
                          {row.value}
                        </span>{' '}
                        <span className="text-text-faint">{row.unit}</span>
                      </span>
                    </li>
                  ))}
                </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!summary && (
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>{t('analytics.landFund.landowners.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {landowners.length === 0 ? (
              <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
            ) : (
            <Table data-testid="analytics-landowners">
              <TableHeader>
                <TableRow className="border-surface-border hover:bg-transparent">
                  <TableHead
                    rowSpan={2}
                    className="h-auto py-2 align-bottom text-text-faint"
                  >
                    {t('analytics.landFund.landowners.columnCompany')}
                  </TableHead>
                  <TableHead
                    colSpan={2}
                    className="h-auto border-l border-surface-border py-2 text-center text-text-faint"
                  >
                    {t('analytics.landFund.landowners.columnAssigned')}
                  </TableHead>
                  <TableHead
                    colSpan={2}
                    className="h-auto border-l border-surface-border py-2 text-center text-text-faint"
                  >
                    {t('analytics.landFund.landowners.columnFact')}
                  </TableHead>
                </TableRow>
                <TableRow className="border-surface-border hover:bg-transparent">
                  {(['assigned', 'fact'] as const).map((group) =>
                    (['columnArable', 'columnPasture'] as const).map(
                      (column) => (
                        <TableHead
                          key={`${group}-${column}`}
                          className={
                            column === 'columnArable'
                              ? 'h-auto border-l border-surface-border py-2 text-right text-text-faint'
                              : 'h-auto py-2 text-right text-text-faint'
                          }
                        >
                          {t(`analytics.landFund.landowners.${column}`)}
                        </TableHead>
                      ),
                    ),
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {landowners.map((owner) => (
                  <TableRow
                    key={owner.key}
                    data-testid={`analytics-landowner-row-${owner.key}`}
                    className="border-surface-border hover:bg-surface-muted/60"
                  >
                    <TableCell className="py-3.5 font-semibold">
                      {owner.name}
                    </TableCell>
                    <TableCell className="border-l border-surface-border py-3.5 text-right font-extrabold tabular-nums">
                      {owner.assignedArable}
                    </TableCell>
                    <TableCell className="py-3.5 text-right font-extrabold tabular-nums">
                      {owner.assignedPasture}
                    </TableCell>
                    <TableCell className="border-l border-surface-border py-3.5 text-right font-bold tabular-nums text-brand-600">
                      {owner.factArable}
                    </TableCell>
                    <TableCell className="py-3.5 text-right font-bold tabular-nums text-brand-600">
                      {owner.factPasture}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            )}
          </CardContent>
        </Card>
      )}
        </>
      )}
    </section>
  );
};
